import React, { useEffect, useRef, useState } from 'react';
import { SHIELD_PREVIEW_MONTHLY_PRICE_CENTS } from '@privisy/domain';
import { ComplianceCheck } from '../../utils/types';
import { recordShieldInterest } from '../../utils/api';
import { ApiError } from '../../utils/api-error';
import { formatPriceFromCents } from '../../utils/price';
import { T } from '../../designTokens';

/**
 * The checks Privisy Shield would fix, by catalog name, with the plain-words
 * line the offer lists. Shield is a script on the customer's site, so only
 * checks about what the live site does are here; privacy policy wording is
 * never one of them.
 */
export const SHIELD_FIXES: ReadonlyArray<{ check: string; label: string }> = [
  { check: 'Global Privacy Control (GPC) Signal', label: 'Honor Global Privacy Control opt-out signals' },
  { check: 'Do Not Sell or Share Link', label: 'Add the "Do Not Sell or Share" link and opt-out' },
  { check: 'GPC Status Display', label: 'Show visitors their opt-out was applied' },
  { check: 'Third-Party PII Transmission', label: 'Stop personal data reaching ad pixels' },
];

/** The SHIELD_FIXES lines whose check this report fails, or warns on in a way that counts. */
export function shieldFixesFor(checks: ComplianceCheck[]): string[] {
  const open = new Set(
    checks
      .filter((c) => c.status === 'non_compliant' || (c.status === 'warning' && !c.advisory))
      .map((c) => c.name),
  );
  return SHIELD_FIXES.filter((f) => open.has(f.check)).map((f) => f.label);
}

interface ShieldOfferProps {
  reportId: string;
  /** The report's CCPA checks (collectCcpaChecks). Only verdicts are read, so a locked report works. */
  checks: ComplianceCheck[];
}

type Step = 'offer' | 'open' | 'sending' | 'reserved';

/**
 * Fake-door offer for Privisy Shield (docs/economics/decisions.md,
 * 2026-10-21). Shield is not built: the offer says so as soon as it is opened,
 * charges nothing, and only collects an address for the launch email. Each
 * step is recorded once per report so the test can be read out
 * (scripts/shield-fake-door.sql). Renders nothing when the report fails none
 * of the checks Shield would fix.
 */
export default function ShieldOffer({ reportId, checks }: ShieldOfferProps) {
  const fixes = shieldFixesFor(checks);
  const [step, setStep] = useState<Step>('offer');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const shownFor = useRef<string | null>(null);
  const hasFixes = fixes.length > 0;

  useEffect(() => {
    if (!hasFixes || shownFor.current === reportId) return;
    shownFor.current = reportId;
    // A lost tally is not worth bothering the reader about.
    recordShieldInterest(reportId, 'shown').catch(() => undefined);
  }, [reportId, hasFixes]);

  if (!hasFixes) return null;

  const price = formatPriceFromCents(SHIELD_PREVIEW_MONTHLY_PRICE_CENTS);

  const handleOpen = () => {
    setStep('open');
    recordShieldInterest(reportId, 'clicked').catch(() => undefined);
  };

  const handleReserve = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim() === '' || step === 'sending') return;
    setStep('sending');
    setError(null);
    try {
      await recordShieldInterest(reportId, 'reserved', email.trim());
      setStep('reserved');
    } catch (err) {
      setStep('open');
      setError(
        err instanceof ApiError && err.apiCode === 'INVALID_REQUEST'
          ? 'Enter a valid email address.'
          : 'Something went wrong. Please try again.',
      );
    }
  };

  const issueWord = fixes.length === 1 ? 'issue' : 'issues';

  return (
    <section
      aria-labelledby="shield-offer-heading"
      data-testid="shield-offer"
      style={{
        background: T.white,
        border: `1px solid ${T.gray200}`,
        borderLeft: `2px solid ${T.cobalt600}`,
        borderRadius: T.radiusLg,
        padding: 20,
        margin: '32px 0',
      }}
    >
      <h3
        id="shield-offer-heading"
        style={{ fontFamily: T.fontBody, fontSize: 18, fontWeight: 600, color: T.gray900, margin: 0 }}
      >
        Fix {fixes.length === 1 ? 'this' : `these ${fixes.length}`} automatically
      </h3>
      <p style={{ fontFamily: T.fontBody, fontSize: 15, color: T.gray700, margin: '8px 0 0' }}>
        Privisy Shield is one script you add to your site. It would fix {fixes.length} {issueWord} in this
        report on your live site, and Privisy rescans every week to confirm the fixes hold.
      </p>
      <ul style={{ fontFamily: T.fontBody, fontSize: 15, color: T.gray800, margin: '12px 0 0', paddingLeft: 20 }}>
        {fixes.map((label) => (
          <li key={label} style={{ marginTop: 4 }}>{label}</li>
        ))}
      </ul>

      {step === 'offer' && (
        <button type="button" onClick={handleOpen} className="legacy-cta__button" style={{ marginTop: 16 }}>
          Fix these with Shield
        </button>
      )}

      {(step === 'open' || step === 'sending') && (
        <form onSubmit={handleReserve} aria-label="Get notified when Shield launches" style={{ marginTop: 16 }}>
          <p role="status" style={{ fontFamily: T.fontBody, fontSize: 15, color: T.gray800, margin: 0 }}>
            <strong>Shield isn&apos;t available yet.</strong> We&apos;re building it now. It will cost {price} a
            month, weekly monitoring included. Leave your email and we&apos;ll tell you when it launches. Nothing
            is charged today.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            <label htmlFor="shield-offer-email" className="sr-only">
              Email address
            </label>
            <input
              id="shield-offer-email"
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                flex: '1 1 220px', padding: '8px 12px', fontSize: 14,
                border: `1px solid ${T.gray300}`, borderRadius: 6, color: T.gray900,
              }}
            />
            <button type="submit" disabled={email.trim() === '' || step === 'sending'} className="legacy-cta__button">
              {step === 'sending' ? 'Saving…' : 'Notify me'}
            </button>
          </div>
          <p style={{ fontFamily: T.fontBody, fontSize: 12, color: T.gray500, margin: '8px 0 0' }}>
            We&apos;ll only use this address to tell you about Shield.
          </p>
          {error && (
            <p role="alert" style={{ fontFamily: T.fontBody, fontSize: 13, color: T.red700, margin: '8px 0 0' }}>
              {error}
            </p>
          )}
        </form>
      )}

      {step === 'reserved' && (
        <p role="status" style={{ fontFamily: T.fontBody, fontSize: 15, color: T.emerald700, margin: '16px 0 0' }}>
          You&apos;re on the list. We&apos;ll email you when Shield launches.
        </p>
      )}
    </section>
  );
}
